/**
 * Financial Close components
 *
 * Reusable Vue components for the financial close module.
 * Import from `@/modules/financialclose/components`.
 * Read this catalog before adding a new component under this module.
 *
 * Not exported (internal to a folder — do not duplicate):
 * - BulkUpload/BulkUploadFrame.vue — download-template + drop-zone shell
 * - BulkUpload/BulkUploadDialog.vue — centered dialog chrome for BulkUpload
 * - SetupBulkUpload uses BulkUploadFrame (do not re-implement the shell)
 * - FcConversation/Comments.vue — @-mention thread used by FcConversation
 * - PaginationFilter/PaginationFilterBuilder.vue — condition editor used by PaginationFilter
 * - PaginationFilter/PaginationFilterSort.vue — sort column list used by PaginationFilter
 * - PaginationFilter/PaginationFilterUtils.js — import from that file; not a catalog component
 */

/** Dialog to bulk-edit L1 approvers and L2 sign-off approvers (location-scoped users). */
export { default as BulkApproverEditorDialog } from './BulkApproverEditorDialog/BulkApproverEditorDialog.vue';

/** Dialog to bulk-edit preparers (location-scoped users). */
export { default as BulkPreparerEditorDialog } from './BulkPreparerEditorDialog/BulkPreparerEditorDialog.vue';

/** Table column visibility menu. Apply is temporary; Save persists preferences. */
export { default as CustomizeColumnMenu } from './CustomizeColumnMenu/CustomizeColumnMenu.vue';

/** Toolbar menu that filters tasks by type (All Tasks + task-type options). */
export { default as FilterMenu } from './FilterMenu/FilterMenu.vue';

/** Empty-state message when notifications are disabled. Prop: `message`. */
export { default as NotificationNotEnabledMessage } from './NotificationNotEnabledMessage/NotificationNotEnabledMessage.vue';

export {
  NotificationsTable,
  NotificationStatusToggle,
  NotificationKindBadge,
  NotificationScheduleCell,
  NotificationNextRunsTooltip,
  ScheduledAtField,
  NotificationNameField,
  NotificationRecipientChips,
  DueDateOffsetField,
  NotificationActivityTable,
  NotificationDeleteAction,
  NotificationActionIcon,
} from './Notifications';

/** Table pagination: page controls and a go-to-page input. Emits `paginated-data`. */
export { default as Pagination } from './Pagination/Pagination.vue';

/** Toolbar filters, sort, and page controls. Parent fetches; emits `apply`, `save`, and `page-change`. */
export { default as PaginationFilter } from './PaginationFilter/PaginationFilter.vue';

/** Activity-log dialog. Fetches by `resourceId`, or consolidated logs via `moduleName`. */
export { default as ActivityLog } from './ActivityLog/ActivityLog.vue';

/** Optional multi-line field (task name, purpose, instruction). Variants: card, plain. */
export { default as CollapsibleTextAreaField } from './CollapsibleTextAreaField/CollapsibleTextAreaField.vue';

/** Optional single-line field. Collapsed state is a label plus expand. */
export { default as CollapsibleTextField } from './CollapsibleTextField/CollapsibleTextField.vue';

/** Preparer / manager role menu for horizon and task-list views. v-model is the role value. */
export { default as RoleSwitcher } from './RoleSwitcher/RoleSwitcher.vue';

/** Searchable user multi-select with avatar chips. Variants: assignment-dialog, add-task, combobox. */
export { default as UserChipMultiSelect } from './UserChipMultiSelect/UserChipMultiSelect.vue';

/** Debounced toolbar search input. v-model is the query string. */
export { default as SearchBar } from './SearchBar/SearchBar.vue';

/** Draft multi-select (search, checkboxes, select-all). Commits on Apply. */
export { default as SearchableMultiSelectDropdown } from './SearchableMultiSelectDropdown/SearchableMultiSelectDropdown.vue';

/** Dual-month calendar for a from/to period. Emits `{ from, to }` on Done. */
export { default as PeriodRangePicker } from './PeriodRangePicker/PeriodRangePicker.vue';

/** Toolbar filter for year / month tokens (`YYYY` or `YYYY-MM`). Commits on Apply. */
export { default as PeriodFilterDropdown } from './PeriodFilterDropdown/PeriodFilterDropdown.vue';

/** Cycle setup error: trigger dot plus dialog showing `errorMessage`. */
export { default as CycleErrorDetail } from './CycleErrorDetail/CycleErrorDetail.vue';

/** Tab list. `tabs` is `{ id, label, count? }[]`. Variants: pill (default), underline. */
export { default as FcTabSwitcher } from './FcTabSwitcher/FcTabSwitcher.vue';

/** Tooltip wrapper. Variants: default (global bubble), rich (Figma card + centered arrow). */
export { default as FcTooltip } from './FcTooltip/FcTooltip.vue';

/** Clamped text with a tooltip when the content overflows `lines`. */
export { default as ExpandableText } from './ExpandableText/ExpandableText.vue';

/** Inline labels (default one), plus a +N badge whose tooltip lists the rest. */
export { default as OverflowList } from './OverflowList/OverflowList.vue';

/** Task card: ref, location, priority, overdue, status. `navigationMode` route or emit. */
export { default as TaskCardDetail } from './TaskCardDetail/TaskCardDetail.vue';

/** Comment thread with @-mentions. Forwards attrs and listeners to Comments.vue. */
export { default as FcConversation } from './FcConversation/FcConversation.vue';

/** User initials avatar. Props: `name`, optional `email`, `size`. */
export { default as FcUserAvatar } from './FcUserAvatar/FcUserAvatar.vue';

/** System-actor glyph avatar. `approved` adds the approval badge. */
export { default as FcSystemActorAvatar } from './FcSystemActorAvatar/FcSystemActorAvatar.vue';

/** Attachment drop zone with a file list, blocked-extension checks, and remove. */
export { default as FcDragDropFile } from './FcDragDropFile/FcDragDropFile.vue';

/** Clickable pipeline stage chips with status indicators and counts. Emits `select-stage`. */
export { default as PipelineStatusChips } from './PipelineStatusChips/PipelineStatusChips.vue';

/** Outlined text field plus a string menu. A typed value not in the list is valid. */
export { default as FreeformSelect } from './FreeformSelect/FreeformSelect.vue';

/** Generic chip multi-select with a Select All checkbox kept in sync with the selection. */
export { default as ChipMultiSelect } from './ChipMultiSelect/ChipMultiSelect.vue';

/**
 * Shared Excel bulk upload (checklist, trial balance, bulk edit, information request).
 * Managed mode parses, validates, and uploads. External mode renders the preview
 * while the parent owns parse/upload.
 */
export { default as BulkUpload } from './BulkUpload/BulkUpload.vue';

/**
 * Setup masters bulk upload shell (COA, Task List, Information Manager).
 * Drawer + BulkUploadFrame; parent owns parse/validate/submit and #preview.
 */
export { default as SetupBulkUpload } from './SetupBulkUpload/SetupBulkUpload.vue';
